'use client';

/**
 * ArtifactImage — the inline preview for an `image` artifact.
 *
 * Images are the one artifact kind that must NOT be collapsed behind an "Open"
 * button. The whole point of asking Meridian to crop a photo is to see whether
 * the crop is right; a row of text saying "cropped-headshot.webp · 84 KB" makes
 * the user do a round-trip to find out. So the card renders the picture itself
 * and the viewer is the zoom, not the reveal.
 *
 * SECURITY: `src` is checked against `isRenderableArtifactImageSrc` before it
 * reaches the DOM. Only Hub's own same-origin image route is allowed — an
 * arbitrary URL in an `<img src>` is an outbound request from the user's browser
 * (a tracking pixel, an IP leak), and a persisted part could have been edited in
 * the database even though the model cannot create this kind.
 *
 * The route is authenticated and returns bytes, so a plain `<img>` works with
 * the session cookie and nothing signed leaks into the DOM. `next/image` is
 * deliberately not used: these are one-off private artifacts, so the optimizer
 * would add a second authenticated hop for no caching benefit.
 */

import React, { useState } from 'react';
import { ImageOffIcon } from 'lucide-react';
import { isRenderableArtifactImageSrc } from '../../lib/artifacts/types.js';
import type { ArtifactData } from '../../lib/artifacts/types.js';

export interface ArtifactImageProps {
  artifact: ArtifactData;
  /** `preview` sizes for the message flow; `full` fills the viewer. */
  variant?: 'preview' | 'full';
  className?: string;
}

function UnavailableImage({ reason }: { reason: string }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-muted/40 p-6 text-center"
      data-testid="artifact-image-unavailable"
    >
      <ImageOffIcon className="size-5 text-muted-foreground" aria-hidden />
      <p className="text-xs text-muted-foreground">{reason}</p>
    </div>
  );
}

export function ArtifactImage({ artifact, variant = 'preview', className }: ArtifactImageProps) {
  const [failed, setFailed] = useState(false);

  if (!isRenderableArtifactImageSrc(artifact.content)) {
    return <UnavailableImage reason="This image can't be displayed." />;
  }

  if (failed) {
    // The presigned bytes behind an artifact expire (30-day lifecycle), and a
    // reloaded old conversation will hit that. Say so plainly rather than
    // leaving a broken-image glyph.
    return <UnavailableImage reason="This image is no longer available." />;
  }

  const sizeClasses =
    variant === 'full'
      ? 'max-h-[70vh] w-auto max-w-full'
      : 'max-h-80 w-auto max-w-full';

  return (
    <img
      src={artifact.content}
      alt={artifact.title}
      {...(artifact.image
        ? { width: artifact.image.width, height: artifact.image.height }
        : {})}
      onError={() => setFailed(true)}
      // The checkerboard shows through transparency, so a cut-out reads as
      // transparent rather than as an accidentally white background.
      className={[
        'rounded-md border border-border object-contain',
        'bg-[length:16px_16px] bg-[position:0_0,8px_8px]',
        '[background-image:linear-gradient(45deg,rgba(128,128,128,0.15)_25%,transparent_25%,transparent_75%,rgba(128,128,128,0.15)_75%),linear-gradient(45deg,rgba(128,128,128,0.15)_25%,transparent_25%,transparent_75%,rgba(128,128,128,0.15)_75%)]',
        sizeClasses,
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="artifact-image"
    />
  );
}
